import type {
  StockAdjustmentResponse,
  StockCountResponse,
  StockMovementKindWire,
  StockStatus,
  StockTransferResponse,
} from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { movementPillClass, stockBadge } from './stock-format';

/**
 * The stock wave's status vocabulary in one place — the purchases
 * status-chips pattern. Every colour decision that is pure lives in
 * stock-format.ts; these are the components that wear it.
 */
const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold';

/** BR-INV-05-03 — an OK row renders NOTHING at all, not an "OK" pill. */
export function StockStatusBadge({ status }: { status: StockStatus }) {
  const { t } = useTranslation();
  const badge = stockBadge(status);
  if (!badge) return null;
  return <span className={cn(CHIP, badge.className)}>{t(badge.key)}</span>;
}

export function MovementKindPill({ kind }: { kind: StockMovementKindWire }) {
  const { t } = useTranslation();
  return <span className={cn(CHIP, movementPillClass(kind))}>{t(`mv.kind.${kind}`)}</span>;
}

const VOUCHER_CLASSES: Readonly<Record<StockAdjustmentResponse['status'], string>> = {
  draft: 'bg-muted text-muted-foreground',
  posted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  // Faded on the FILL, not on the chip: `opacity-70` dragged the ink toward
  // the surface along with its own background, taking the pair to 3.69:1
  // against a bar of 4.5. Same colours, same 70%, ink left alone — 5.59:1.
  // See components/status-chip.tsx and test/faded-chip-contrast.test.ts.
  cancelled: 'bg-red-100/70 text-red-700 dark:bg-red-950/70 dark:text-red-300',
};

export function VoucherStatusChip({ status }: { status: StockAdjustmentResponse['status'] }) {
  const { t } = useTranslation();
  return <span className={cn(CHIP, VOUCHER_CLASSES[status])}>{t(`common.status.${status}`)}</span>;
}

/** INV-08 §5.3's direction: Stock In green, Stock Out red. */
export function DirectionChip({ direction }: { direction: 'in' | 'out' }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        CHIP,
        direction === 'in'
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
          : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
      )}
    >
      {t(direction === 'in' ? 'adj.dirIn' : 'adj.dirOut')}
    </span>
  );
}

const APPROVAL_CLASSES: Readonly<Record<'pending' | 'approved' | 'rejected', string>> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

export function ApprovalChip({ status }: { status: 'pending' | 'approved' | 'rejected' }) {
  const { t } = useTranslation();
  return <span className={cn(CHIP, APPROVAL_CLASSES[status])}>{t(`adj.approval.${status}`)}</span>;
}

const COUNT_CLASSES: Readonly<Record<StockCountResponse['status'], string>> = {
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  posted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  // Faded on the FILL, not on the chip: `opacity-70` dragged the ink toward
  // the surface along with its own background, taking the pair to 3.69:1
  // against a bar of 4.5. Same colours, same 70%, ink left alone — 5.59:1.
  // See components/status-chip.tsx and test/faded-chip-contrast.test.ts.
  cancelled: 'bg-red-100/70 text-red-700 dark:bg-red-950/70 dark:text-red-300',
};

export function CountStatusChip({ status }: { status: StockCountResponse['status'] }) {
  const { t } = useTranslation();
  return <span className={cn(CHIP, COUNT_CLASSES[status])}>{t(`counts.status.${status}`)}</span>;
}

/** INV-07 §5.1: draft grey, in transit amber, completed green, cancelled red. */
const TRANSFER_CLASSES: Readonly<Record<StockTransferResponse['status'], string>> = {
  draft: 'bg-muted text-muted-foreground',
  in_transit: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  // Faded on the FILL, not on the chip: `opacity-70` dragged the ink toward
  // the surface along with its own background, taking the pair to 3.69:1
  // against a bar of 4.5. Same colours, same 70%, ink left alone — 5.59:1.
  // See components/status-chip.tsx and test/faded-chip-contrast.test.ts.
  cancelled: 'bg-red-100/70 text-red-700 dark:bg-red-950/70 dark:text-red-300',
};

export function TransferStatusChip({ status }: { status: StockTransferResponse['status'] }) {
  const { t } = useTranslation();
  return <span className={cn(CHIP, TRANSFER_CLASSES[status])}>{t(`trf.status.${status}`)}</span>;
}

export function TransferModeChip({ mode }: { mode: 'instant' | 'two_step' }) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, 'bg-muted text-muted-foreground')}>
      {t(mode === 'instant' ? 'trf.modeInstant' : 'trf.modeTwoStep')}
    </span>
  );
}

/** The filter-bar toggle every stock screen uses (status, kinds, warehouses). */
export function Chip({
  active,
  onClick,
  children,
  dotClass,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  dotClass?: string | undefined;
  className?: string | undefined;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-input text-muted-foreground hover:text-foreground',
        className,
      )}
    >
      {dotClass ? <span className={cn('size-2 rounded-full', dotClass)} aria-hidden /> : null}
      {children}
    </button>
  );
}
